import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpenCheck, Download, ArrowRight } from "lucide-react";

/**
 * Public, unauthenticated beta tester guide. Renders the markdown shipped
 * with the app at /beta-guide/guide.md so the team can share a single URL
 * with testers instead of attaching a PDF.
 */
export default function BetaGuide() {
  const [md, setMd] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/beta-guide/guide.md")
      .then((r) => {
        if (!r.ok) throw new Error("Couldn't load the guide. Try refreshing.");
        return r.text();
      })
      .then(setMd)
      .catch((e) => setError(e.message));
  }, []);

  return (
    <div className="min-h-screen rm-paper">
      {/* Slim public header (no nav) */}
      <header
        className="px-4 md:px-8 py-4 flex items-center justify-between flex-wrap gap-3"
        style={{ borderBottom: "3px solid #0F1B2D", background: "#FFF2EC" }}
      >
        <Link to="/welcome" className="flex items-center gap-2" data-testid="beta-guide-logo-link">
          <span className="font-display text-2xl">
            <span style={{ color: "#FF6A00" }}>R</span>esmo
          </span>
          <span className="rm-tag" data-testid="beta-guide-tag">
            <BookOpenCheck size={11} /> Beta Tester Guide
          </span>
        </Link>
        <div className="flex gap-2">
          <a
            href="/beta-guide/guide.md"
            download="resmo-beta-guide.md"
            className="rm-btn rm-btn-ghost text-xs px-3 py-2"
            data-testid="beta-guide-download-btn"
          >
            <Download size={12} /> Download .md
          </a>
          <Link
            to="/welcome"
            className="rm-btn rm-btn-primary text-xs px-3 py-2"
            data-testid="beta-guide-open-app-btn"
          >
            Open Resmo <ArrowRight size={12} />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {error && (
          <div
            className="p-4 rounded font-mono-rm text-sm"
            style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
            data-testid="beta-guide-error"
          >
            {error}
          </div>
        )}
        {!md && !error && (
          <div className="text-center py-16 font-mono-rm text-sm" style={{ color: "#475569" }}>
            Loading the guide...
          </div>
        )}
        {md && (
          <article className="rm-guide" data-testid="beta-guide-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                img: (props) => (
                  <img
                    {...props}
                    alt={props.alt || ""}
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      border: "3px solid #0F1B2D",
                      boxShadow: "5px 5px 0 0 #FFC145",
                      margin: "20px 0",
                    }}
                    loading="lazy"
                  />
                ),
                a: (props) => (
                  <a {...props} target={props.href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
                    {props.children}
                  </a>
                ),
              }}
            >
              {md}
            </ReactMarkdown>
          </article>
        )}
      </main>

      <footer
        className="px-4 md:px-8 py-6 text-center font-mono-rm text-xs"
        style={{ color: "#475569", borderTop: "2px dashed #0F1B2D" }}
      >
        Resmo beta · You can share this link with any tester · Last updated 2026-02-22
      </footer>

      {/* Styling for the markdown content */}
      <style>{`
        .rm-guide h1 {
          font-family: var(--font-display, 'Bebas Neue', sans-serif);
          font-size: 2.5rem;
          line-height: 1;
          text-transform: uppercase;
          margin: 0 0 24px;
          color: #0F1B2D;
        }
        .rm-guide h2 {
          font-family: var(--font-display, 'Bebas Neue', sans-serif);
          font-size: 1.75rem;
          line-height: 1.1;
          text-transform: uppercase;
          margin: 40px 0 16px;
          color: #0F1B2D;
          border-bottom: 3px solid #FFC145;
          padding-bottom: 4px;
        }
        .rm-guide h3 {
          font-family: var(--font-display, 'Bebas Neue', sans-serif);
          font-size: 1.25rem;
          text-transform: uppercase;
          margin: 24px 0 10px;
          color: #FF6A00;
        }
        .rm-guide p {
          font-size: 0.95rem;
          line-height: 1.7;
          margin: 0 0 16px;
          color: #0F1B2D;
        }
        .rm-guide ul, .rm-guide ol {
          padding-left: 24px;
          margin: 0 0 16px;
        }
        .rm-guide li {
          font-size: 0.95rem;
          line-height: 1.7;
          margin: 4px 0;
          color: #0F1B2D;
        }
        .rm-guide a {
          color: #FF6A00;
          text-decoration: underline;
          text-underline-offset: 3px;
          font-weight: 600;
        }
        .rm-guide strong {
          color: #0F1B2D;
          font-weight: 700;
        }
        .rm-guide blockquote {
          border-left: 4px solid #FF6A00;
          background: #FFFBEB;
          padding: 12px 16px;
          margin: 16px 0;
          border-radius: 4px;
        }
        .rm-guide blockquote p {
          margin: 0;
          font-size: 0.92rem;
        }
        .rm-guide code {
          background: #F1F5F9;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: var(--font-mono, 'JetBrains Mono', monospace);
          font-size: 0.85rem;
          color: #0F1B2D;
        }
        .rm-guide table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          font-size: 0.9rem;
        }
        .rm-guide th, .rm-guide td {
          border: 2px solid #0F1B2D;
          padding: 8px 12px;
          text-align: left;
          vertical-align: top;
        }
        .rm-guide th {
          background: #FFC145;
          font-weight: 700;
        }
        .rm-guide td {
          background: white;
        }
        .rm-guide hr {
          border: 0;
          border-top: 3px dashed #FFC145;
          margin: 32px 0;
        }
      `}</style>
    </div>
  );
}
